import { useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from 'convex/react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../convex/_generated/api';
import {
  CHALLENGE_PROJECT_DRAFT_PATH,
  CHALLENGE_PROJECT_SUBMISSION_PATH,
} from '../../../shared/challengeProjectSeed';
import { localizeChallenge } from './challengeLocalization';
import {
  countNonEmptyLines,
  countWords,
  formatConstraintLabel,
  formatReviewQueueLabel,
  formatSubmissionStatus,
} from './challengeUtils';
import './challenges.css';

function formatSubmittedAt(value, language) {
  if (!value) return null;
  try {
    return new Intl.DateTimeFormat(language || 'en', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(value));
  } catch {
    return new Date(value).toLocaleString();
  }
}

export default function ChallengeSubmissionPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const detail = useQuery(api.challenges.getBySlug, slug ? { slug } : 'skip');
  const submission = useQuery(
    api.challenges.getUserSubmission,
    detail?.challenge?._id ? { challengeId: detail.challenge._id } : 'skip',
  );
  const withdrawSubmission = useMutation(api.challenges.withdrawSubmission);
  const startProjectFromChallenge = useMutation(api.challenges.startProjectFromChallenge);
  const [message, setMessage] = useState('');
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [isOpeningProject, setIsOpeningProject] = useState(false);

  const challenge = localizeChallenge(detail?.challenge, t);
  const submittedAtLabel = useMemo(
    () => formatSubmittedAt(submission?.submittedAt, i18n.language),
    [i18n.language, submission?.submittedAt],
  );

  if (detail === undefined || submission === undefined) {
    return (
      <div className="chw-shell">
        <div className="chw-card">
          <h2>{t('challengesRuntime.submission.loadingTitle', { defaultValue: 'Loading submission…' })}</h2>
          <p>{t('challengesRuntime.submission.loadingSubtitle', { defaultValue: 'Preparing your submission status.' })}</p>
        </div>
      </div>
    );
  }

  if (!detail || !submission) {
    return (
      <div className="chw-shell">
        <div className="chw-card">
          <h2>{t('challengesRuntime.submission.notFoundTitle', { defaultValue: 'Submission not found' })}</h2>
          <p>{t('challengesRuntime.submission.notFoundSubtitle', { defaultValue: 'There is no saved submission for this challenge yet.' })}</p>
          <div className="chw-actions">
            <Link to={`/challenges/${slug}`} className="btn btn-secondary">
              {t('challengesRuntime.actions.backToChallenge', { defaultValue: 'Back to challenge' })}
            </Link>
            <Link to={`/challenges/${slug}/write`} className="btn btn-primary">
              {t('challengesRuntime.actions.startDraft', { defaultValue: 'Start draft' })}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (submission.status === 'draft' || submission.status === 'rejected') {
    return <Navigate to={`/challenges/${slug}/write`} replace />;
  }

  const isJudgesQueue = detail.challenge.reviewQueue === 'judges';
  const canWithdraw = submission.status === 'submitted' || submission.status === 'under_review';
  const linkedProjectId = submission.projectId || detail.viewer.linkedProjectId || null;
  const titleKey = submission.status === 'approved'
    ? 'approvedTitle'
    : submission.status === 'under_review'
      ? 'underReviewTitle'
      : 'submittedTitle';
  const bodyKey = submission.status === 'approved'
    ? 'approvedBody'
    : submission.status === 'under_review'
      ? 'underReviewBody'
      : 'submittedBody';

  const handleWithdraw = async () => {
    setMessage('');
    setIsWithdrawing(true);
    try {
      await withdrawSubmission({ submissionId: submission._id });
      setMessage(t('challengesRuntime.submission.withdrawSuccess', { defaultValue: 'Your submission is back in draft mode.' }));
    } catch (error) {
      setMessage(error?.data?.message || error?.message || t('challengesRuntime.submission.withdrawFailed', { defaultValue: 'Could not move this submission back to draft.' }));
    } finally {
      setIsWithdrawing(false);
    }
  };

  const handleCreateProject = async () => {
    setMessage('');
    setIsOpeningProject(true);
    try {
      const result = await startProjectFromChallenge({ challengeId: detail.challenge._id });
      navigate(`/projects/${result.projectId}`);
    } catch (error) {
      setMessage(
        error?.data?.message
          || error?.message
          || t('challengesRuntime.submission.projectLaunchFailed', {
            defaultValue: 'Could not open this challenge as a project.',
          }),
      );
    } finally {
      setIsOpeningProject(false);
    }
  };

  return (
    <div className="chw-shell">
      <div className="chw-header">
        <div>
          <div className="chw-breadcrumbs">
            <Link to="/challenges">{t('landing.nav.challenges', { defaultValue: 'Challenges' })}</Link>
            <span>/</span>
            <Link to={`/challenges/${challenge.slug}`}>{challenge.title}</Link>
            <span>/</span>
            <span>{t('challengesRuntime.submission.breadcrumb', { defaultValue: 'Submission' })}</span>
          </div>
          <h1>{challenge.title}</h1>
          <p>{challenge.description}</p>
        </div>
      </div>

      {message ? <div className="chw-banner">{message}</div> : null}

      <div className="chw-grid chw-grid-status">
        <section className="chw-editor-panel chw-status-panel">
          <div className="chw-status-kicker">
            {isJudgesQueue
              ? t('challengesRuntime.reviewQueues.judges', { defaultValue: 'Judges review' })
              : t('challengesRuntime.reviewQueues.community', { defaultValue: 'Community review' })}
          </div>
          <h2>{t(`challengesRuntime.submission.${titleKey}`, { defaultValue: 'Submission received' })}</h2>
          <p className="chw-status-copy">
            {t(`challengesRuntime.submission.${bodyKey}`, {
              defaultValue: isJudgesQueue
                ? 'Your entry is with the judges now. It stays private until review is finished, and you can still move it back to draft if you want to revise it.'
                : 'Your entry is now in the live community queue. You can check the public feed or pull it back into draft mode if you want to revise it.',
            })}
          </p>

          <div className="chw-status-actions">
            {!isJudgesQueue ? (
              <Link to={`/challenges/${challenge.slug}/feed`} className="btn btn-primary">
                {t('challengesRuntime.actions.viewFeed', { defaultValue: 'View feed' })}
              </Link>
            ) : (
              <Link to={`/challenges/${challenge.slug}`} className="btn btn-primary">
                {t('challengesRuntime.actions.backToChallenge', { defaultValue: 'Back to challenge' })}
              </Link>
            )}
            <Link to="/projects/challenges" className="btn btn-secondary">
              {t('challengesRuntime.workspace.nav', { defaultValue: 'My challenges' })}
            </Link>
            {canWithdraw ? (
              <button className="btn btn-secondary" onClick={handleWithdraw} disabled={isWithdrawing}>
                {isWithdrawing
                  ? t('challengesRuntime.submission.withdrawing', { defaultValue: 'Moving to draft…' })
                  : t('challengesRuntime.actions.withdrawToDraft', { defaultValue: 'Withdraw to draft' })}
              </button>
            ) : null}
            {linkedProjectId ? (
              <Link to={`/projects/${linkedProjectId}`} className="btn btn-secondary">
                {t('challengesRuntime.actions.continueProject', { defaultValue: 'Continue project' })}
              </Link>
            ) : (
              <button className="btn btn-secondary" onClick={handleCreateProject} disabled={isOpeningProject}>
                {isOpeningProject
                  ? t('challengesRuntime.submission.openingProject', { defaultValue: 'Opening project…' })
                  : t('challengesRuntime.actions.startAsProject', { defaultValue: 'Start as project' })}
              </button>
            )}
          </div>

          <div className="chw-status-note">
            {isJudgesQueue
              ? t('challengesRuntime.submission.judgesNote', {
                  defaultValue: 'Judge-reviewed entries stay out of the public feed until they are approved.',
                })
              : t('challengesRuntime.submission.communityNote', {
                  defaultValue: 'Community-reviewed entries appear in the public feed after submission.',
            })}
          </div>

          {linkedProjectId ? (
            <div className="chw-project-bridge">
              <div className="chw-project-bridge-copy">
                <div className="chw-submission-preview-label">
                  {t('challengesRuntime.submission.projectBridgeLabel', { defaultValue: 'Continue this beyond the challenge' })}
                </div>
                <p>
                  {t('challengesRuntime.submission.projectBridgeBody', {
                    defaultValue: 'This prompt already has a linked project workspace. Keep drafting there, then run reader preview, audience test, or other studios when you are ready.',
                  })}
                </p>
              </div>
              <div className="chw-status-actions" style={{ marginTop: 0 }}>
                <Link
                  to={`/projects/${linkedProjectId}/editor?path=${encodeURIComponent(CHALLENGE_PROJECT_SUBMISSION_PATH)}`}
                  className="btn btn-secondary"
                >
                  {t('challengesRuntime.write.openSubmissionFile', { defaultValue: 'Open challenge-submission.md' })}
                </Link>
                <Link
                  to={`/projects/${linkedProjectId}/editor?path=${encodeURIComponent(CHALLENGE_PROJECT_DRAFT_PATH)}`}
                  className="btn btn-secondary"
                >
                  {t('challengesRuntime.write.openScratchDraft', { defaultValue: 'Open scratch draft' })}
                </Link>
                <Link to={`/projects/${linkedProjectId}`} className="btn btn-secondary">
                  {t('challengesRuntime.actions.openProjectHome', { defaultValue: 'Open project home' })}
                </Link>
                <Link to={`/projects/${linkedProjectId}/reader-preview`} className="btn btn-secondary">
                  {t('projects.stages.openReaderPreview', { defaultValue: 'Open Reader Preview' })}
                </Link>
                <Link to={`/projects/${linkedProjectId}/draft-review`} className="btn btn-secondary">
                  {t('projects.stages.runDraftReview', { defaultValue: 'Run Draft Review' })}
                </Link>
              </div>
            </div>
          ) : (
            <div className="chw-project-bridge">
              <div className="chw-project-bridge-copy">
                <div className="chw-submission-preview-label">
                  {t('challengesRuntime.submission.projectBridgeLabel', { defaultValue: 'Turn this into a bigger project' })}
                </div>
                <p>
                  {t('challengesRuntime.submission.projectBridgeEmptyBody', {
                    defaultValue: 'Create a linked project workspace if this challenge is becoming a chapter, a story seed, or the start of a larger manuscript.',
                  })}
                </p>
              </div>
              <div className="chw-status-actions" style={{ marginTop: 0 }}>
                <button className="btn btn-secondary" onClick={handleCreateProject} disabled={isOpeningProject}>
                  {isOpeningProject
                    ? t('challengesRuntime.submission.openingProject', { defaultValue: 'Opening project…' })
                    : t('challengesRuntime.actions.startAsProject', { defaultValue: 'Start as project' })}
                </button>
              </div>
            </div>
          )}

          <div className="chw-submission-preview">
            <div className="chw-submission-preview-label">
              {t('challengesRuntime.submission.previewLabel', { defaultValue: 'Submitted entry' })}
            </div>
            <pre className="chw-submission-content">{submission.content}</pre>
          </div>
        </section>

        <aside className="chw-sidebar">
          <div className="chw-card">
            <span className="ch-section-label">{t('challengesRuntime.labels.queue', { defaultValue: 'Queue' })}</span>
            <strong>{formatReviewQueueLabel(detail.challenge.reviewQueue, t)}</strong>
          </div>
          <div className="chw-card">
            <span className="ch-section-label">{t('challengesRuntime.write.currentStatus', { defaultValue: 'Current status' })}</span>
            <strong>{formatSubmissionStatus(submission.status, t)}</strong>
          </div>
          <div className="chw-card">
            <span className="ch-section-label">{t('challengesRuntime.submission.submittedAt', { defaultValue: 'Submitted at' })}</span>
            <strong>{submittedAtLabel || t('challengesRuntime.submission.justNow', { defaultValue: 'Just now' })}</strong>
          </div>
          <div className="chw-card">
            <span className="ch-section-label">{t('challengesRuntime.labels.constraint', { defaultValue: 'Constraint' })}</span>
            <strong>{formatConstraintLabel(detail.challenge, t)}</strong>
          </div>
          <div className="chw-card">
            <span className="ch-section-label">{t('challengesRuntime.write.wordCount', { defaultValue: 'Word count' })}</span>
            <strong>{submission.wordCount || countWords(submission.content)}</strong>
          </div>
          <div className="chw-card">
            <span className="ch-section-label">{t('challengesRuntime.write.lineCount', { defaultValue: 'Line count' })}</span>
            <strong>{submission.lineCount || countNonEmptyLines(submission.content)}</strong>
          </div>
        </aside>
      </div>
    </div>
  );
}
